import { useEffect, useMemo, useRef, useState } from 'react'
import { config } from './config'
import { Loading } from './auth'

// ── API types (subset of the /api/predict response we visualize) ──────────────

type ConfigRow = {
  color: string | null
  interior: string | null
  rim: string | null
  trim: string | null
  quantity: number
  weight_share: number
}

type StepConf = {
  step: string
  ess: number
  confidence: number
  rim_from_package: boolean
}

type Block = {
  rank: number
  packages: string[]
  single_options: string[]
  base_options: string[]
  rows: ConfigRow[]
  n_cars: number
  weight_share: number
  overall_confidence: number
  confidence_label: 'HIGH' | 'MEDIUM' | 'LOW'
  step_confidences: StepConf[]
}

type DataQuality = {
  total_orders: number
  bto_orders: number
  bto_ratio: number
  effective_bto_weight: number
  low_bto: boolean
}

type PredictResponse = {
  model_codes: string[]
  model_group: string
  n_cars: number
  blocks: Block[]
  data_quality: DataQuality
  option_descriptions: Record<string, string>
}

type ModelStat = {
  model_group: string
  model_description: string
  codes: string[]
  order_count: number
}

// ── Tunable parameters (defaults mirror PredictorConfig) ──────────────────────

type Params = {
  recencyHalfLifeDays: number
  weightBto: number
  weightBtoNoProd: number
  weightBts: number
  pkgWeight: number
  colorWeight: number
  interiorWeight: number
  rimWeight: number
  kBlocks: number
  mRowsPerBlock: number
  blockCoverageTarget: number
  minRowShare: number
  singleOptionThreshold: number
  enableOptionClusters: boolean
  optionMinSignatureShare: number
  optionCoverageTarget: number
  maxOptionBlocksPerBundle: number
  minBlockShare: number
  optionCrossWeight: number
  essThreshold: number
}

const DEFAULT_PARAMS: Params = {
  recencyHalfLifeDays: 180,
  weightBto: 1.0,
  weightBtoNoProd: 0.9,
  weightBts: 0.4,
  pkgWeight: 2.0,
  colorWeight: 1.0,
  interiorWeight: 0.8,
  rimWeight: 0.5,
  kBlocks: 10,
  mRowsPerBlock: 12,
  blockCoverageTarget: 0.85,
  minRowShare: 0.05,
  singleOptionThreshold: 0.4,
  enableOptionClusters: true,
  optionMinSignatureShare: 0.1,
  optionCoverageTarget: 0.75,
  maxOptionBlocksPerBundle: 3,
  minBlockShare: 0.03,
  optionCrossWeight: 0.3,
  essThreshold: 50,
}

function requestBody(modelCodes: string[], nCars: number, p: Params) {
  return {
    model_codes: modelCodes,
    n_cars: nCars,
    k_blocks: p.kBlocks,
    m_rows_per_block: p.mRowsPerBlock,
    block_coverage_target: p.blockCoverageTarget,
    min_row_share: p.minRowShare,
    ess_threshold: p.essThreshold,
    single_option_threshold: p.singleOptionThreshold,
    pkg_weight: p.pkgWeight,
    color_weight: p.colorWeight,
    interior_weight: p.interiorWeight,
    rim_weight: p.rimWeight,
    weight_bto: p.weightBto,
    weight_bto_no_prod: p.weightBtoNoProd,
    weight_bts: p.weightBts,
    recency_half_life_days: p.recencyHalfLifeDays,
    enable_option_clusters: p.enableOptionClusters,
    option_min_signature_share: p.optionMinSignatureShare,
    option_coverage_target: p.optionCoverageTarget,
    max_option_blocks_per_bundle: p.maxOptionBlocksPerBundle,
    min_block_share: p.minBlockShare,
    option_cross_weight: p.optionCrossWeight,
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const SEGMENT_COLORS = [
  '#0653B6', '#2E7BD6', '#5A9BE0', '#83B4EA', '#4A5568',
  '#718096', '#A0AEC0', '#1A365D', '#3B6FB0', '#8A97AD',
]
const segColor = (i: number) => SEGMENT_COLORS[i % SEGMENT_COLORS.length]
const pct = (n: number) => `${(n * 100).toFixed(0)}%`

const CONF_STYLE: Record<Block['confidence_label'], string> = {
  HIGH: 'bg-bmw-ocean text-white',
  MEDIUM: 'border border-[#E0A200] bg-[#FFF6E0] text-[#8A5A00]',
  LOW: 'border border-[#C0392B] bg-[#FDECEA] text-[#B03A2E]',
}

const STEP_LABEL: Record<string, string> = {
  packages: 'Bundle', color: 'Color', interior: 'Interior',
  rim: 'Rim', trim: 'Trim', options: 'Options',
}

// ── Reusable controls ─────────────────────────────────────────────────────────

function Slider(props: {
  label: string; value: number; min: number; max: number; step: number
  onChange: (v: number) => void; fmt?: (v: number) => string
}) {
  const { label, value, min, max, step, onChange, fmt } = props
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-[0.05em] text-[#666]">{label}</span>
        <span className="tabular-nums text-[11px] font-bold text-black">{fmt ? fmt(value) : value}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="h-1 w-full cursor-pointer accent-bmw-ocean"
      />
    </label>
  )
}

function Toggle(props: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-[0.05em] text-[#666]">{props.label}</span>
      <div className="flex border border-[#CFD3DB] p-0.5 text-[10px] font-bold">
        {[['On', true], ['Off', false]].map(([lbl, on]) => (
          <button
            key={String(on)} type="button" onClick={() => props.onChange(on as boolean)}
            className={`px-2 py-0.5 uppercase tracking-[0.04em] ${props.value === on ? 'bg-bmw-ocean text-white' : 'text-[#666] hover:text-black'}`}
          >{lbl as string}</button>
        ))}
      </div>
    </div>
  )
}

function KnobGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-[#E5E7EC]" style={{ borderTop: '2px solid #92A2BD' }}>
      <div className="border-b border-[#E5E7EC] bg-[#FAFBFC] px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-[#444]">{title}</div>
      <div className="space-y-3 p-3">{children}</div>
    </div>
  )
}

// ── Proportional allocation bar ────────────────────────────────────────────────

function AllocationBar(props: {
  segments: { label: string; value: number }[]
  colorAt: (i: number) => string
  selected: number | null
  onSelect: (i: number | null) => void
}) {
  const { segments, colorAt, selected, onSelect } = props
  const total = segments.reduce((a, s) => a + s.value, 0) || 1
  return (
    <div className="flex h-10 w-full overflow-hidden border border-[#E5E7EC]">
      {segments.map((s, i) => (
        <button
          key={i} type="button"
          onClick={() => onSelect(selected === i ? null : i)}
          title={`${s.label} · ${s.value} cars`}
          className="flex items-center justify-center overflow-hidden transition-[width,opacity] duration-500 ease-out"
          style={{
            width: `${(s.value / total) * 100}%`,
            background: colorAt(i),
            opacity: selected === null || selected === i ? 1 : 0.35,
          }}
        >
          {s.value / total > 0.06 && (
            <span className="truncate px-1 text-[10px] font-bold tabular-nums text-white">{s.value}</span>
          )}
        </button>
      ))}
    </div>
  )
}

// ── Per-step ESS strip (the conditioning story, compact) ───────────────────────

function EssStrip({ steps }: { steps: StepConf[] }) {
  const dataSteps = steps.filter(s => !s.rim_from_package)
  const minConf = dataSteps.length ? Math.min(...dataSteps.map(s => s.confidence)) : 1
  return (
    <div className="flex items-end gap-1">
      {steps.map((s, i) => {
        const isBottleneck = !s.rim_from_package && s.confidence === minConf
        return (
          <div key={i} className="flex flex-1 flex-col items-center gap-1">
            <div className="flex h-14 w-full items-end bg-[#F3F5F8]">
              <div
                className="w-full transition-[height] duration-500 ease-out"
                style={{
                  height: `${Math.max(s.confidence * 100, 3)}%`,
                  background: s.rim_from_package ? '#CBD5E0' : isBottleneck ? '#0653B6' : '#83B4EA',
                }}
                title={s.rim_from_package ? 'from package' : `ESS ${s.ess} · conf ${pct(s.confidence)}`}
              />
            </div>
            <span className={`text-[9px] uppercase tracking-[0.04em] ${isBottleneck ? 'font-bold text-bmw-ocean' : 'text-[#999]'}`}>
              {STEP_LABEL[s.step] ?? s.step}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ── Block card ─────────────────────────────────────────────────────────────────

function BlockCard(props: { block: Block; desc: Record<string, string>; accent: string }) {
  const { block, desc, accent } = props
  const d = (code: string | null) => (code ? (desc[code] ?? code) : '—')
  return (
    <div className="border border-[#E5E7EC]" style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="flex flex-wrap items-center gap-2 border-b border-[#E5E7EC] bg-[#FAFBFC] px-4 py-2.5">
        <span className="text-[11px] font-bold tabular-nums text-[#999]">#{block.rank}</span>
        <span className="text-sm font-bold tabular-nums text-black">{block.n_cars} cars</span>
        <span className="text-[11px] text-[#999]">·&nbsp;{pct(block.weight_share)} demand</span>
        <span className={`ml-auto px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] ${CONF_STYLE[block.confidence_label]}`}>
          {block.confidence_label} · {pct(block.overall_confidence)}
        </span>
      </div>

      <div className="space-y-3 px-4 py-3">
        <div className="flex flex-wrap gap-1.5">
          {block.packages.length === 0 && (
            <span className="border border-[#CFD3DB] px-2 py-0.5 text-[10px] uppercase tracking-[0.04em] text-[#999]">Base car · no package</span>
          )}
          {block.packages.map(p => (
            <span key={p} className="bg-[#EEF2F8] px-2 py-0.5 text-[11px] text-black" title={p}>{d(p)}</span>
          ))}
          {block.single_options.map(o => (
            <span key={o} className="border border-bmw-ocean px-2 py-0.5 text-[11px] text-bmw-ocean" title={o}>+ {d(o)}</span>
          ))}
          {block.base_options.map(o => (
            <span key={o} className="px-2 py-0.5 text-[10px] italic text-[#999]" title={o}>{d(o)} (std)</span>
          ))}
        </div>

        <div className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-4 sm:grid-cols-[2fr_1fr]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-[#E5E7EC] text-[9px] font-bold uppercase tracking-[0.08em] text-[#999]">
                  <th className="py-1 pr-3 text-left">Color</th>
                  <th className="py-1 pr-3 text-left">Interior</th>
                  <th className="py-1 pr-3 text-left">Rim</th>
                  <th className="py-1 pr-3 text-left">Trim</th>
                  <th className="py-1 text-right">Cars</th>
                </tr>
              </thead>
              <tbody>
                {block.rows.map((r, i) => (
                  <tr key={i} className="border-b border-[#F0F2F5] last:border-b-0">
                    <td className="py-1 pr-3 text-black">{d(r.color)}</td>
                    <td className="py-1 pr-3 text-[#666]">{d(r.interior)}</td>
                    <td className="py-1 pr-3 text-[#666]">{d(r.rim)}</td>
                    <td className="py-1 pr-3 text-[#666]">{d(r.trim)}</td>
                    <td className="py-1 text-right font-bold tabular-nums text-black">{r.quantity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="mb-1 text-[9px] font-bold uppercase tracking-[0.08em] text-[#999]">Confidence chain</div>
            <EssStrip steps={block.step_confidences} />
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export function AdminModelExplorer() {
  const [models, setModels] = useState<ModelStat[] | null>(null)
  const [modelErr, setModelErr] = useState<string | null>(null)
  const [selectedGroup, setSelectedGroup] = useState<string>('')
  const [nCars, setNCars] = useState(50)
  const [params, setParams] = useState<Params>(DEFAULT_PARAMS)

  const [result, setResult] = useState<PredictResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [selectedBlock, setSelectedBlock] = useState<number | null>(null)

  const set = <K extends keyof Params>(k: K, v: Params[K]) => setParams(p => ({ ...p, [k]: v }))

  // Load the model catalog once.
  useEffect(() => {
    fetch(`${config.apiUrl}/api/admin/stats`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => {
        const ms: ModelStat[] = (d.models ?? []).filter((m: ModelStat) => m.order_count > 0)
        setModels(ms)
        if (ms.length) setSelectedGroup(ms[0].model_group)
      })
      .catch(() => setModelErr('Could not load model catalog'))
  }, [])

  const activeModel = useMemo(
    () => models?.find(m => m.model_group === selectedGroup) ?? null,
    [models, selectedGroup],
  )

  // Debounced live prediction on any input change.
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!activeModel) return
    if (debounce.current) clearTimeout(debounce.current)
    const ctrl = new AbortController()
    debounce.current = setTimeout(async () => {
      setLoading(true)
      setErr(null)
      try {
        const res = await fetch(`${config.apiUrl}/api/predict`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(requestBody(activeModel.codes, nCars, params)),
          signal: ctrl.signal,
        })
        if (!res.ok) throw new Error(`predict failed (${res.status})`)
        setResult(await res.json())
      } catch (e) {
        if ((e as Error).name !== 'AbortError') setErr((e as Error).message)
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => {
      ctrl.abort()
      if (debounce.current) clearTimeout(debounce.current)
    }
  }, [activeModel, nCars, params])

  if (modelErr) return <p className="text-sm text-black">⚠ {modelErr}</p>
  if (!models) return <Loading />

  const dq = result?.data_quality
  const blocks = result?.blocks ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[15px] font-bold uppercase tracking-[0.08em] text-black">Model explorer</h1>
        <p className="mt-1 text-xs text-[#999]">
          Drag any knob and watch the live prediction respond. Every change re-runs the real engine on the selected model.
        </p>
      </div>

      {/* Model + batch */}
      <div className="flex flex-wrap items-end gap-4">
        <label className="block">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#999]">Model</div>
          <select
            value={selectedGroup}
            onChange={e => { setSelectedGroup(e.target.value); setSelectedBlock(null) }}
            className="border border-[#CFD3DB] bg-white px-3 py-1.5 text-sm text-black"
          >
            {models.map(m => (
              <option key={m.model_group} value={m.model_group}>
                {m.model_description} — {m.order_count.toLocaleString()} orders
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#999]">Batch size</div>
          <input
            type="number" min={1} max={500} value={nCars}
            onChange={e => setNCars(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
            className="w-24 border border-[#CFD3DB] bg-white px-3 py-1.5 text-sm tabular-nums text-black"
          />
        </label>
        {activeModel && (
          <span className="pb-2 font-mono text-xs text-[#666]">{activeModel.codes.join(' + ')}</span>
        )}
        <button
          type="button" onClick={() => setParams(DEFAULT_PARAMS)}
          className="ml-auto border border-[#CFD3DB] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-[#666] hover:border-black hover:text-black"
        >Reset knobs</button>
        <span className={`pb-2 text-[11px] uppercase tracking-[0.06em] transition-opacity ${loading ? 'text-bmw-ocean opacity-100' : 'opacity-0'}`}>
          ● updating
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        {/* Knobs */}
        <div className="space-y-4">
          <KnobGroup title="Order weighting">
            <Slider label="Recency half-life" value={params.recencyHalfLifeDays} min={30} max={730} step={10}
              onChange={v => set('recencyHalfLifeDays', v)} fmt={v => `${v} d`} />
            <Slider label="BTO weight" value={params.weightBto} min={0} max={1} step={0.05} onChange={v => set('weightBto', v)} />
            <Slider label="BTO (no prod) weight" value={params.weightBtoNoProd} min={0} max={1} step={0.05} onChange={v => set('weightBtoNoProd', v)} />
            <Slider label="BTS weight" value={params.weightBts} min={0} max={1} step={0.05} onChange={v => set('weightBts', v)} />
          </KnobGroup>
          <KnobGroup title="Similarity weights">
            <Slider label="Package" value={params.pkgWeight} min={0} max={5} step={0.1} onChange={v => set('pkgWeight', v)} />
            <Slider label="Color" value={params.colorWeight} min={0} max={3} step={0.1} onChange={v => set('colorWeight', v)} />
            <Slider label="Interior" value={params.interiorWeight} min={0} max={3} step={0.1} onChange={v => set('interiorWeight', v)} />
            <Slider label="Rim" value={params.rimWeight} min={0} max={3} step={0.1} onChange={v => set('rimWeight', v)} />
          </KnobGroup>
          <KnobGroup title="Blocks & rows">
            <Slider label="Max blocks (K)" value={params.kBlocks} min={1} max={20} step={1} onChange={v => set('kBlocks', v)} />
            <Slider label="Rows per block (M)" value={params.mRowsPerBlock} min={1} max={20} step={1} onChange={v => set('mRowsPerBlock', v)} />
            <Slider label="Block coverage" value={params.blockCoverageTarget} min={0.5} max={1} step={0.01} onChange={v => set('blockCoverageTarget', v)} fmt={pct} />
            <Slider label="Min row share" value={params.minRowShare} min={0.01} max={0.5} step={0.01} onChange={v => set('minRowShare', v)} fmt={pct} />
          </KnobGroup>
          <KnobGroup title="Options & clustering">
            <Toggle label="Option clustering" value={params.enableOptionClusters} onChange={v => set('enableOptionClusters', v)} />
            <Slider label="Single-option threshold" value={params.singleOptionThreshold} min={0.05} max={0.95} step={0.05} onChange={v => set('singleOptionThreshold', v)} fmt={pct} />
            <Slider label="Min signature share" value={params.optionMinSignatureShare} min={0} max={0.5} step={0.01} onChange={v => set('optionMinSignatureShare', v)} fmt={pct} />
            <Slider label="Option coverage" value={params.optionCoverageTarget} min={0.5} max={1} step={0.01} onChange={v => set('optionCoverageTarget', v)} fmt={pct} />
            <Slider label="Max option blocks / bundle" value={params.maxOptionBlocksPerBundle} min={1} max={8} step={1} onChange={v => set('maxOptionBlocksPerBundle', v)} />
            <Slider label="Min block share" value={params.minBlockShare} min={0} max={0.5} step={0.01} onChange={v => set('minBlockShare', v)} fmt={pct} />
            <Slider label="Cross-option borrow" value={params.optionCrossWeight} min={0} max={1} step={0.05} onChange={v => set('optionCrossWeight', v)} />
          </KnobGroup>
          <KnobGroup title="Confidence">
            <Slider label="ESS threshold (50% point)" value={params.essThreshold} min={5} max={1000} step={5} onChange={v => set('essThreshold', v)} />
          </KnobGroup>
        </div>

        {/* Output */}
        <div className="min-w-0 space-y-6">
          {err && <p className="text-sm text-black">⚠ {err}</p>}

          {dq && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { v: dq.total_orders.toLocaleString(), l: 'Orders in pool' },
                { v: dq.bto_orders.toLocaleString(), l: 'BTO orders' },
                { v: pct(dq.bto_ratio), l: 'BTO ratio', warn: dq.low_bto },
                { v: dq.effective_bto_weight.toFixed(2), l: 'Avg BTO weight' },
              ].map(t => (
                <div key={t.l} className="border border-[#E5E7EC] p-3" style={{ borderTop: '2px solid #92A2BD' }}>
                  <div className="text-xl font-bold tabular-nums text-black">{t.v}</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-[0.06em] text-[#999]">
                    {t.l}{t.warn && <span className="ml-1.5 border border-black px-1 text-[8px] font-bold text-black">LOW</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {blocks.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <h2 className="text-xs font-bold uppercase tracking-[0.08em] text-[#444]">Allocation · {blocks.length} blocks → {nCars} cars</h2>
                <span className="text-[10px] text-[#999]">click a block to isolate its rows</span>
              </div>
              <AllocationBar
                segments={blocks.map(b => ({ label: b.packages.join('+') || 'Base', value: b.n_cars }))}
                colorAt={segColor} selected={selectedBlock} onSelect={setSelectedBlock}
              />
              {selectedBlock !== null && blocks[selectedBlock] && (
                <AllocationBar
                  segments={blocks[selectedBlock].rows.map(r => ({
                    label: result?.option_descriptions[r.color ?? ''] ?? r.color ?? '—', value: r.quantity,
                  }))}
                  colorAt={() => segColor(selectedBlock)} selected={null} onSelect={() => {}}
                />
              )}
            </div>
          )}

          <div className="space-y-3">
            {blocks.map((b, i) => (
              <BlockCard key={b.rank} block={b} desc={result!.option_descriptions} accent={segColor(i)} />
            ))}
            {!loading && blocks.length === 0 && !err && (
              <p className="text-xs text-[#999]">No prediction yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
